'use client'

import * as SliderPrimitive from '@radix-ui/react-slider'
import { cn } from '@repo/ui/lib'
import * as React from 'react'

const Slider = React.forwardRef<
  React.ComponentRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SliderPrimitive.Root
    className={cn('relative flex w-full touch-none select-none items-center', className)}
    ref={ref}
    {...props}
  >
    <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-secondary">
      <SliderPrimitive.Range className="absolute h-full bg-brand" />
    </SliderPrimitive.Track>
    <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full bg-background shadow-md shadow-slate-300 ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" />
  </SliderPrimitive.Root>
))
Slider.displayName = SliderPrimitive.Root.displayName
interface RangeSliderProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>,
    'onValueChange' | 'value'
  > {
  defaultValue?: number[]
  onValueChange?: (values: number[]) => void
  value?: number[]
}

const RangeSlider = React.forwardRef<
  React.ComponentRef<typeof SliderPrimitive.Root>,
  RangeSliderProps
>(({ className, defaultValue = [25, 75], onValueChange, value, ...props }, ref) => {
  // Only manage internal state if value is not provided (uncontrolled component)
  const [internalValues, setInternalValues] = React.useState<number[]>(defaultValue)

  // Use either the controlled value or internal state
  const currentValues = value !== undefined ? value : internalValues

  const handleValueChange = (newValues: number[]) => {
    // Only update internal state if we're not controlled
    if (value === undefined) {
      setInternalValues(newValues)
    }

    // Always call the callback
    if (onValueChange) {
      onValueChange(newValues)
    }
  }

  return (
    <SliderPrimitive.Root
      className={cn('relative flex w-full touch-none select-none items-center', className)}
      defaultValue={value !== undefined ? undefined : defaultValue}
      onValueChange={handleValueChange}
      ref={ref}
      value={currentValues}
      {...props}
    >
      <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-secondary">
        <SliderPrimitive.Range className="absolute h-full bg-brand" />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb
        aria-label="Min value"
        className="block h-5 w-5 rounded-full bg-background shadow-md shadow-slate-300 ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
      />
      <SliderPrimitive.Thumb
        aria-label="Max value"
        className="block h-5 w-5 rounded-full bg-background shadow-md shadow-slate-300 ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
      />
    </SliderPrimitive.Root>
  )
})
RangeSlider.displayName = 'RangeSlider'

RangeSlider.displayName = SliderPrimitive.Root.displayName

export { RangeSlider, Slider }
